/** Meant to be used by a developer to test submissions */
import { LocalWebClientStorage, OwlClient, WebClientStorage } from '../owl';
import {
  CarrierImplementationData,
  ClientValidationState,
  PigeonClient,
  ValidationCommandWsMessage,
} from '../pigeon';
import { URL } from 'iso-url';
import {
  ArtifactType,
  CarrierDataExtracted,
  FieldType,
  Form,
  JiraSearchResponse,
  LineType,
  ObjectUrl,
  User,
} from '../../types';
import { Carrier } from '../../owl';
import {
  OwlSubmissionInfo,
  JiraStatuses,
  JiraPriorities,
  CreateTicketResponse,
} from '../../types/owl';
import { CanaryResults, CarrierLoginData, Flow } from '../../types/pigeon';
import { createUUID } from '../../utils';

const TEST_PHONE_LAST_4 = '4323';

export interface RunningSubmissionAlerts {
  onSubmissionId?: (submissionId: string) => void;
}

export enum Environment {
  LOCAL = 'local',
  STAGE = 'stage',
  PROD = 'prod',
}

export const isEnvronment = (env?: string): env is Environment => {
  if (!env) {
    return false;
  }
  return Object.values<string>(Environment).includes(env);
};

const owlUrlMap: Record<Environment, string> = {
  local: 'http://owl.insurgrid.localhost:8080/api/v1',
  stage: 'https://owl-staging.insurgrid.com/api/v1',
  prod: 'https://owl.insurgrid.com/api/v1',
};

const pigeonUrlMap: Record<Environment, string> = {
  local: 'http://localhost:3001',
  stage: 'https://pigeon-staging.insurgrid.com',
  prod: 'https://pigeon.insurgrid.com',
};

export enum ValidationLevel {
  /** The carrier has full validation */
  FULLY_IMPLEMENTED,
  /** The carrier only has to check to see if validation didn't work */
  PARTIALLY_IMPLEMENTED,
  /** There is no logic for this carrier */
  UNIMPLEMENTED,
}

export interface CarrierInfo {
  slug: string;
  validationLevel: ValidationLevel;
  hasAutomation: boolean;
  /** Automated MFA Implemented */
  mfaImplemented: boolean;
  percentAutoApproved: number;
  /** Whether any submissions came in with MFA **/
  hasMfa: boolean;
  /** Percentage of Submissions With Successful Auto MFA **/
  percentMfa: number;
  hasDataExtraction: boolean;
  numberOfApprovedSubmissions: number;
}

/**
 * A client that contains an owl and pigeon client
 * Abstracts out connection logic to make it simple to run and rerun submissions
 * Auto handles MFA when our MFA number is found.
 */
export class DeveloperClient {
  private owlUrl: string;
  private pigeonUrl: string;
  private pigeonEnv: Environment;
  private owlEnv: Environment;

  public pigeonClient: PigeonClient;
  public owlClient: OwlClient;

  public clientId?: string;

  private readonly storage: WebClientStorage;

  constructor(
    pigeonEnv: Environment,
    owlEnv: Environment,
    storageClass: { new (): WebClientStorage } = LocalWebClientStorage,
  ) {
    this.pigeonEnv = pigeonEnv;
    this.owlEnv = owlEnv;

    this.owlUrl = owlUrlMap[owlEnv];
    this.pigeonUrl = pigeonUrlMap[pigeonEnv];

    this.storage = new storageClass();
    this.owlClient = new OwlClient(this.owlUrl, this.storage);
    this.pigeonClient = new PigeonClient(this.pigeonUrl, this.storage, () =>
      this.storage.getItem(this.getEnvToken()),
    );
  }

  // =======================================
  //    READ
  // =======================================

  /** Get a link for owl's submission overview page
   * @param submissionId The ID of the submission
   * */
  getSubmissionLink(submissionId: string) {
    const owlOrigin = new URL(this.owlUrl).origin;
    return `${owlOrigin}/admin/client-submission-workflow/submission-detail/${submissionId}`;
  }

  /** Returns true if connected to pigeon's websocket */
  get isConnected(): boolean {
    return this.pigeonClient.isConnected;
  }

  get isLoggedIntoOwl(): boolean {
    return Boolean(this.owlClient.auth.getAuthToken());
  }

  getEnvToken() {
    return this.pigeonEnv === Environment.PROD
      ? 'owlToken-prod'
      : 'owlToken-stage';
  }

  getBrowserStates() {
    return this.pigeonClient.getActiveBrowserSessions(this.getEnvToken());
  }

  getKeepAliveJobs() {
    return this.pigeonClient.getKeepAliveJobs(this.getEnvToken());
  }

  getCanaryResults(
    from: number,
    to: number,
    carrier: string,
  ): Promise<CanaryResults | void> {
    return this.pigeonClient.getCanaryResults(from, to, carrier);
  }

  getTraceUrls(submissionId: string): Promise<ObjectUrl[]> {
    return this.pigeonClient.getBucketObjects(submissionId, ArtifactType.Trace);
  }

  getHarUrls(submissionId: string): Promise<ObjectUrl[]> {
    return this.pigeonClient.getBucketObjects(submissionId, ArtifactType.HAR);
  }

  getPolicyInfo(submissionId: string): Promise<OwlSubmissionInfo> {
    return this.owlClient.developer.getPolicyInfo(submissionId);
  }

  searchForJiraTickets({
    statuses = [],
    searchTerm,
    priorities,
    page = 1,
    pageSize = 15,
  }: {
    statuses?: JiraStatuses[];
    searchTerm?: string;
    priorities?: JiraPriorities[];
    page?: number;
    pageSize?: number;
  }): Promise<JiraSearchResponse[] | null> {
    return this.owlClient.developer.searchForJiraTickets({
      statuses,
      searchTerm,
      priorities,
      page,
      pageSize,
    });
  }

  async getAllTickets() {
    const allTickets: JiraSearchResponse[] = [];
    let page = 1;
    let data = await this.searchForJiraTickets({ page });
    while (data?.length && page <= 10) {
      page++;
      allTickets.push(...data);
      data = await this.searchForJiraTickets({ page });
    }
    return allTickets;
  }

  /** Maps carrier name to carrier */
  private allCarriersMap: Record<string, Carrier> | null = null;

  private async getAllOwlCarriers() {
    if (!this.allCarriersMap) {
      const allCarriers = (
        await this.owlClient.carrier.fetchList({
          query: { insuranceLine: LineType.Personal },
        })
      ).data;
      this.allCarriersMap = allCarriers.reduce(
        (acc, curr) => ({ ...acc, [curr.name]: curr }),
        {},
      );
    }
    return this.allCarriersMap;
  }

  private carrierImplementationMap: Record<
    string,
    CarrierImplementationData
  > | null = null;

  private async getCarrierImplData() {
    if (!this.carrierImplementationMap) {
      const carrierImplementationData =
        await this.pigeonClient.getCarriersImplementationData();
      this.carrierImplementationMap = carrierImplementationData.reduce(
        (acc, curr) => ({ ...acc, [curr.slug]: curr }),
        {},
      );
    }

    return this.carrierImplementationMap;
  }

  async getAllCarriers(daysBack: number, page: number, perPage: number) {
    const carrierImplementationMap = await this.getCarrierImplData();
    const allCarrierMap = await this.getAllOwlCarriers();

    const {
      data: carrierSubmissionData,
      meta: { last_page },
    } = await this.owlClient.developer.getCarrierSubmissionData(
      daysBack,
      page,
      perPage,
    );

    const carriersInfo = carrierSubmissionData.map((submissionData) => {
      const carrier = allCarrierMap[submissionData.carrier_name] ?? null;
      const basicData = carrierImplementationMap[carrier.slug] ?? null;

      const carrierInfo: CarrierInfo = {
        validationLevel:
          basicData?.validationLevel ?? ValidationLevel.UNIMPLEMENTED,
        hasAutomation: basicData?.hasAutomation ?? false,
        mfaImplemented: basicData?.mfaImplemented ?? false,
        numberOfApprovedSubmissions:
          submissionData?.approved_submission_count ?? 0,
        hasMfa: submissionData
          ? submissionData?.number_of_mfa_required_logins > 0
          : false,
        slug: carrier.slug,
        hasDataExtraction: submissionData
          ? submissionData.percentage_approved_with_data_extracted > 0
          : false,
        percentMfa: submissionData
          ? submissionData.number_of_mfa_required_logins /
            submissionData.total_submission_count
          : 0,
        percentAutoApproved: submissionData?.percentage_auto_approved ?? 0,
      };

      return carrierInfo;
    });

    // Sort by number of submissions then by name
    carriersInfo.sort((carA, carB) => {
      if (
        carA.numberOfApprovedSubmissions === carB.numberOfApprovedSubmissions
      ) {
        return carA.slug.localeCompare(carB.slug);
      }
      return (
        carB.numberOfApprovedSubmissions - carA.numberOfApprovedSubmissions
      );
    });

    return {
      carriers: carriersInfo,
      lastPage: last_page,
    };
  }

  getPolicyList(query: {
    carriers?: string;
    page?: number;
    perPage?: number;
    policyTypes?: string;
  }) {
    return this.owlClient.developer.getPoliciesList({ query });
  }

  getPolicyDecPage(policyUUID: string) {
    return this.owlClient.developer.getPolicyDecPage({
      args: { policyUUID },
    });
  }

  getSubmissions() {
    return this.owlClient.developer.getSubmissions;
  }

  getPolicyTypes() {
    return this.owlClient.auth.getPolicyTypes;
  }

  getCarrierList() {
    return this.owlClient.carrier.fetchList;
  }

  getPigeonEnv() {
    return this.pigeonEnv;
  }

  getOwlEnv() {
    return this.owlEnv;
  }

  getTestAgency() {
    if (this.owlEnv === 'local' || this.owlEnv === 'stage') {
      return 'caleb-northcott-agency';
    } else {
      return 'caleb-northcott-agency';
    }
  }

  // =======================================
  //   WRITE / UPDATE
  // =======================================

  /** INS-1391: acked, capability-checked validation command (replaces sendFormData) */
  private async sendCommand(
    access: Omit<
      ValidationCommandWsMessage,
      'requestId' | 'data' | 'expectedGeneration' | 'expectedChallengeId'
    >,
    state: ClientValidationState,
    data: Record<string, string>,
  ) {
    const ack = await this.pigeonClient.sendValidationCommand({
      ...access,
      requestId: createUUID(),
      expectedGeneration: state.generation ?? 0,
      expectedChallengeId: state.challengeId,
      data,
    });
    if (ack.status !== 'accepted' && ack.status !== 'completed') {
      throw new Error(`Pigeon did not accept the command: ${ack.status}`);
    }
  }

  /** Connect to pigeon's websocket */
  async connectToPigeonWebsocket() {
    try {
      await this.pigeonClient.connectToSocket();
    } catch (err: any) {
      throw new Error(`Failed to connect to pigeon websocket: ${err}`);
    }
  }

  async owlLogin(username: string, password: string) {
    await this.owlClient.auth.login({
      email: username,
      password: password,
    });
    const token = this.owlClient.auth.getAuthToken();
    if (typeof token === 'string') {
      this.storage.setItem(`owlToken-${this.owlEnv}`, token);
    }
  }

  async owlLogout() {
    await this.owlClient.auth.logout();
    this.storage.removeItem(`owlToken-${Environment.STAGE}`);
    this.storage.removeItem(`owlToken-${Environment.PROD}`);
  }

  owlCurrentUser(): Promise<User | null> {
    return this.owlClient.auth.getCurrentUser();
  }

  changePigeonEnv(env: Environment) {
    this.pigeonEnv = env;
    this.pigeonUrl = pigeonUrlMap[env];
    this.pigeonClient = new PigeonClient(this.pigeonUrl, this.storage, () =>
      this.storage.getItem(this.getEnvToken()),
    );
  }

  async changeOwlEnv(env: Environment) {
    this.owlEnv = env;
    this.owlUrl = owlUrlMap[env];
    this.owlClient = new OwlClient(this.owlUrl, this.storage);
    const authToken = this.storage.getItem(`owlToken-${env}`);
    if (typeof authToken === 'string') {
      await this.owlClient.auth.setAuthToken(authToken);
    } else {
      this.owlClient.auth.clearAuthToken();
    }
  }

  getAutoApprovalRates() {
    return this.owlClient.developer.getAutoApprovalRates();
  }

  getDataExtractedByCarrier(
    carrierSlug: string,
  ): Promise<CarrierDataExtracted | undefined> {
    return this.owlClient.developer.getCarrierDataExtracted(carrierSlug);
  }

  createJiraTicket(
    summary: string,
    clientSubmissionUuids: string[],
    description: string,
  ): Promise<CreateTicketResponse | null> {
    return this.owlClient.developer.createJiraTicket({
      summary,
      clientSubmissionUuids,
      description,
    });
  }

  addSubmissionIdsToTicket(
    ticket: string,
    clientSubmissionUuids: string[],
  ): Promise<void | null> {
    return this.owlClient.developer.addSubmissionsToTicket({
      ticket,
      clientSubmissionUuids,
    });
  }

  async createSubmission(
    carrierSlug: string,
    loginData: CarrierLoginData,
    handleMfa: {
      mfaCode: () => Promise<string>;
      mfaSelect?: (form: Form) => Promise<string>;
    },
    agency?: string,
  ): Promise<string | undefined> {
    if (this.pigeonEnv === Environment.PROD) {
      const onProd = confirm(
        'Are you sure you want to run this submission on production?',
      );
      if (!onProd) {
        return;
      }
    }
    await this.connectToPigeonWebsocket();

    const client = await this.owlClient.client.createRandom(
      agency ?? this.getTestAgency(),
    );
    const { uuid: clientId } = client;

    return new Promise(async (resolve, reject) => {
      // INS-1391: a refused command must fail the run, not hang or crash it
      try {
        const created = await this.pigeonClient.createValidationAttempt({
          carrierSlug,
          clientId,
          lineType: LineType.Personal,
        });
        const access = {
          submissionId: created.submissionId,
          capability: created.capability,
          clientId,
          carrierSlug,
          lineType: LineType.Personal,
        };
        let validationState: ClientValidationState = created;

        validationState = await new Promise((resolve, reject) => {
          this.sendCommand(access, validationState, loginData as any).catch(
            reject,
          );

          this.pigeonClient.onValidationData(resolve);
        });

        if (validationState.validationOver) {
          resolve(validationState.submissionId);
        }

        // Handle MFA
        if (
          (validationState.flow === Flow.MfaSelect ||
            validationState.flow === Flow.MfaCodeEntry) &&
          validationState.form
        ) {
          let mfaOption = '';
          if (!handleMfa.mfaSelect) {
            mfaOption =
              DeveloperClient.getMfaLabel(validationState.form)?.value ?? '';
          } else {
            mfaOption = await handleMfa.mfaSelect(validationState.form);
          }
          const formKey = validationState.form?.[0]?.name ?? '';

          if (!mfaOption || !validationState.flow) {
            throw new Error(
              'MFA error' + JSON.stringify(validationState, null, ''),
            );
          }
          validationState = await new Promise<ClientValidationState>(
            (resolve, reject) => {
              this.sendCommand(access, validationState, {
                [formKey]: mfaOption,
              }).catch(reject);
              this.pigeonClient.onValidationData(resolve);
            },
          );

          const formName = validationState.form?.[0]?.name ?? '';

          if (!validationState.flow) {
            throw new Error('MFA Error');
          }

          while (!validationState.validationOver) {
            if (validationState.errorMessage) {
              await alert(validationState.errorMessage);
            }
            const mfaCodeResponse = await handleMfa.mfaCode();
            validationState = await new Promise<ClientValidationState>(
              (resolve, reject) => {
                this.sendCommand(access, validationState, {
                  [formName]: mfaCodeResponse,
                }).catch(reject);
                this.pigeonClient.onValidationData(resolve);
              },
            );
          }
          resolve(validationState.submissionId);
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  /** Runs through the entire submission and returns the submission result
   * @param carrierSlug
   * @param loginData
   * @param getMfaCode If mfa is encountered, this is called to retrieve the code form the user
   * @param alerts Object filled with callback functions that alerts of progress of submission
   */
  async runSubmission(
    carrierSlug: string,
    loginData: CarrierLoginData,
    getMfaCode: () => Promise<string>,
    alerts?: RunningSubmissionAlerts,
  ): Promise<OwlSubmissionInfo> {
    await this.connectToPigeonWebsocket();

    const client = await this.owlClient.client.createRandom(
      this.getTestAgency(),
    );
    const { uuid: clientId } = client;

    // @ts-ignore
    return new Promise(async (resolve, reject) => {
      // INS-1391: a refused command must fail the run, not hang or crash it
      try {
        const started = await this.pigeonClient.startValidationAttempt({
          carrierSlug,
          clientId,
          submissionId: `canary-${createUUID()}`,
          lineType: LineType.Personal,
        });
        const access = {
          submissionId: started.submissionId,
          capability: started.capability ?? '',
          clientId,
          carrierSlug,
          lineType: LineType.Personal,
        };
        let validationState: ClientValidationState = started;

        validationState = await new Promise((resolve, reject) => {
          this.sendCommand(access, validationState, loginData as any).catch(
            reject,
          );

          this.pigeonClient.onValidationData(resolve);
        });

        const submissionId = validationState.submissionId;

        if (!submissionId) {
          return false;
        }

        this.pigeonClient.subscribeToSubmissionUpdates(
          submissionId,
          async () => {
            const subInfo = await this.owlClient.developer.getPolicyInfo(
              submissionId,
            );
            resolve(subInfo);
          },
        );

        alerts?.onSubmissionId?.(submissionId);

        // Handle MFA
        if (
          (validationState.flow === Flow.MfaSelect ||
            validationState.flow === Flow.MfaCodeEntry) &&
          validationState.form
        ) {
          const mfaOption = DeveloperClient.getMfaLabel(validationState.form);
          const formKey = validationState.form?.[0]?.name ?? '';

          if (!mfaOption || !validationState.flow) {
            throw new Error(
              'MFA error' + JSON.stringify(validationState, null, ''),
            );
          }
          validationState = await new Promise<ClientValidationState>(
            (resolve, reject) => {
              this.sendCommand(access, validationState, {
                [formKey]: mfaOption.value,
              }).catch(reject);
              this.pigeonClient.onValidationData(resolve);
            },
          );
          const mfaCodeResponse = await getMfaCode();

          const formName = validationState.form?.[0]?.name ?? '';

          if (!validationState.flow) {
            throw new Error('MFA Error');
          }

          await this.sendCommand(access, validationState, {
            [formName]: mfaCodeResponse,
          });
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Takes the submission id and fetches its credentials. Reruns the submission with the credentials
   * @param owlEnvForSubmission The environment where the current submission lives
   * @param submissionId The submissionId that will be cloned for this submission
   * @param getMfaCode If mfa is encountered, this is called to retrieve the code form the user
   * @param alerts Object filled with callback functions that alerts of progress of submission
   */
  async rerunSubmission(
    owlEnvForSubmission: Environment,
    submissionId: string,
    getMfaCode: () => Promise<string>,
    alerts?: RunningSubmissionAlerts,
  ) {
    const owlClient = new OwlClient(
      owlUrlMap[owlEnvForSubmission],
      this.storage,
    );

    const authToken = this.storage.getItem(`owlToken-${owlEnvForSubmission}`);

    if (!(typeof authToken === 'string')) {
      throw new Error('Not logged in to owl');
    }

    owlClient.developer.setAuthToken(authToken);

    const { username, password, carrierSlug } =
      await owlClient.developer.getPolicyInfo(submissionId);

    const newSubmissionInfo = await this.runSubmission(
      carrierSlug,
      {
        password,
        username,
      },
      getMfaCode,
      alerts,
    );

    if (newSubmissionInfo.status !== 'complete') {
      throw new Error(`Failed with status ${newSubmissionInfo.status}`);
    }

    return newSubmissionInfo;
  }

  async waitForSubmissionToFinish(
    submissionId: string,
  ): Promise<OwlSubmissionInfo> {
    return new Promise((resolve) => {
      this.pigeonClient.subscribeToSubmissionUpdates(submissionId, async () => {
        const subInfo = await this.owlClient.developer.getPolicyInfo(
          submissionId,
        );
        resolve(subInfo);
      });
    });
  }

  /** Returns the form option that contains our MFA number */
  // @ts-ignore
  private static getMfaLabel(mfaForm: Form) {
    for (const ele of mfaForm) {
      if (ele.type === FieldType.Radio) {
        const op = ele.options.find((op) => {
          return (
            op.label.includes(TEST_PHONE_LAST_4) ||
            op.value.includes(TEST_PHONE_LAST_4)
          );
        });
        if (op) {
          return op;
        }
      }
    }
  }
}
